import React from 'react';
import formatCurrency from '../../../utils/format-currency';
import * as styles from './stat-tiles.style';
import useCountUp from './use-count-up';

export type StatTilesVariant = 'sales' | 'rental' | 'administrator';

export type StatTilesStatistics = {
  totalSoldAndAuctioned?: number | null;
  averageDaysOnMarket?: number | null;
  averageSoldPrice?: number | null;
  totalForRent?: number | null;
  totalLeased?: number | null;
};

export type StatTilesProps = {
  variant: StatTilesVariant;
  statistics: StatTilesStatistics;
  caveat?: string;
};

const EM_DASH = '—';

type TileConfig = {
  label: string;
  value?: number | null;
  format?: (value: number) => string;
};

const tilesForVariant = (
  variant: StatTilesVariant,
  statistics: StatTilesStatistics,
): TileConfig[] => {
  if (variant === 'rental') {
    return [
      { label: 'For rent', value: statistics.totalForRent },
      { label: 'Leased', value: statistics.totalLeased },
    ];
  }

  if (variant === 'sales') {
    return [
      { label: 'Total sales', value: statistics.totalSoldAndAuctioned },
      {
        label: 'Avg days listed',
        value: statistics.averageDaysOnMarket,
        format: (value) => `${Math.floor(value)}`,
      },
      {
        label: 'Avg sold price',
        value: statistics.averageSoldPrice,
        format: formatCurrency,
      },
    ];
  }

  return [];
};

const formatDisplay = (
  animated: number | null,
  format?: (value: number) => string,
): string => {
  if (animated === null) {
    return EM_DASH;
  }
  return format ? format(animated) : `${Math.round(animated)}`;
};

const Tile = ({
  label,
  value,
  format,
  index,
}: TileConfig & { index: number }): JSX.Element => {
  // A missing metric and a genuine zero are indistinguishable to the agent -
  // both mean "nothing to show" here, so both fall back to the em dash.
  const target = value || null;
  const animated = useCountUp(target);

  const display = formatDisplay(animated, format);

  return (
    <div css={styles.tile(index)}>
      <div css={styles.value}>{display}</div>
      <div css={styles.label}>{label}</div>
    </div>
  );
};

const StatTiles = ({
  variant,
  statistics,
  caveat,
}: StatTilesProps): JSX.Element | null => {
  const tiles = tilesForVariant(variant, statistics);

  if (tiles.length === 0) {
    return null;
  }

  return (
    <div data-testid="stat-tiles">
      <div css={styles.row}>
        {tiles.map((tile, index) => (
          <Tile key={tile.label} {...tile} index={index} />
        ))}
      </div>
      {caveat && <p css={styles.caveat}>{caveat}</p>}
    </div>
  );
};

export default StatTiles;
